import { useEffect, useState } from 'react';
import { useCalibrationState } from '../context/CalibrationPageContext';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';

import SensorGroup from './update_test_boxes/SensorGroup';
import { saveBoxSensors, fetchLoadTestBoxes, fetchBoxSensors } from '../services/api';


function UpdateTestBoxes() {
  const { testData, setTestData } = useCalibrationState();
  const [boxes, setBoxes] = useState([]);
  const [selectedTestBox, setTestBox] = useState('');
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null); // { type, text }

  // Load the real box list for the selector, and select the first box.
  useEffect(() => {
    fetchLoadTestBoxes()
      .then((list) => {
        setBoxes(list);
        if (list.length > 0) setTestBox(String(list[0].id));
      })
      .catch((err) => setFeedback({ type: 'error', text: err.message }));
  }, []);

  // Load the selected box's current sensor assignments into testData.
  useEffect(() => {
    if (!selectedTestBox) return;
    fetchBoxSensors(selectedTestBox)
      .then((groups) => setTestData((prev) => ({ ...prev, [selectedTestBox]: groups })))
      .catch((err) => setFeedback({ type: 'error', text: err.message }));
  }, [selectedTestBox]);

  const handleTestBoxSelect = (e) => { setFeedback(null); setTestBox(e.target.value); };

  const updateSensorGroup = (sensorKey, updatedRows) => {
    if (!testData[selectedTestBox]) return;
    const updated = { ...testData };
    updated[selectedTestBox][sensorKey] = updatedRows.map((r, i) => ({ ...r, id: i + 1 }));
    setTestData(updated);
  }

  const handleSaveAssignments = async () => {
    const box = testData[selectedTestBox];
    if (!box) return;
    // Send serial numbers per group; the backend resolves them to sensor Ids.
    const payload = Object.fromEntries(
      Object.entries(box).map(([group, rows]) => [group, rows.map((r) => r.SerialNumber)])
    );
    setSaving(true);
    setFeedback(null);
    try {
      await saveBoxSensors(selectedTestBox, payload);
      setFeedback({ type: 'success', text: 'Sensor assignments saved.' });
    } catch (err) {
      setFeedback({ type: 'error', text: err.message });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Box display='flex' height='100%' width='100%' alignItems='center' justifyContent='center' sx={{}}>
      <Box
        justifySelf='center'
        height='100%'
        width={{ xl: '60%', sm: '70%' }}
        sx={{
          pb: 0,
          pt: 0.5,
          // overflowY: 'auto',
          scrollbarWidth: 'none'
        }}
      >
        <Stack
          direction='column'
          spacing={1}
          sx={{
            height: { xs: 'auto', lg: '82px', xl: '85px' }
          }}
        >
          {/* Wraps on a phone: the box picker and Save button don't fit one row */}
          <Stack direction='row' alignItems='center' justifyContent='space-between' flexWrap='wrap' rowGap={1}>
          <FormControl
            size='small'
            display='flex'
            sx={{
              borderRadius: 1,
              backgroundColor: 'background.paper',
              width: { xs: '100%', sm: '220px' },
            }}
          >
            <InputLabel>Select Load Test Box</InputLabel>
            <Select
              value={selectedTestBox}
              label='Select Load Test Box'
              MenuProps={{ disableScrollLock: true }}
              onChange={handleTestBoxSelect}
              sx={{
                fontSize: { xl: 16, md: 14, sm: 12 }
              }}
            >
              {boxes.map((b) => (
                <MenuItem key={b.id} value={String(b.id)}>{b.Name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Stack direction='row' alignItems='center' spacing={1}>
            {feedback && (
              <Typography variant='caption' sx={{ color: feedback.type === 'success' ? 'success.main' : 'error.main' }}>
                {feedback.text}
              </Typography>
            )}
            <Button
              size='small'
              variant='contained'
              disabled={saving || !testData[selectedTestBox]}
              onClick={handleSaveAssignments}
            >
              {saving ? <CircularProgress size={16} color='inherit' /> : 'Save Assignments'}
            </Button>
          </Stack>
          </Stack>

          <Grid
            container
            sx={{
              p: 0.5,
              borderRadius: 1,
              backgroundColor: 'secondary.main',
              color: 'white'
            }}
          >
            <Grid item xs={4} justifyItems='center'>
              <Typography variant='body1' sx={{ fontSize: { xl: '16px', md: '14px', sm: '12px', xs: '11px' } }}>
                Serial Number
              </Typography>
            </Grid>

            <Grid item xs={4} justifyItems='center'>
              <Typography variant='body1' sx={{ fontSize: { xl: '16px', md: '14px', sm: '12px', xs: '11px' } }}>
                Calibration Factor
              </Typography>
            </Grid>

            <Grid item xs={4} justifyItems='center'>
              <Typography variant='body1' sx={{ fontSize: { xl: '16px', md: '14px', sm: '12px', xs: '11px' } }}>
                Test Date
              </Typography>
            </Grid>
          </Grid>
        </Stack>

        <Box
          sx={{
            overflowY: 'auto',
            // mt: 1,
            // height: { xl: '87.5%', sm: '75%' },
            height: { xs: 'auto', sm: `calc(100% - 82px)`, xl: `calc(100% - 85px)` },
            scrollbarWidth: 'none'
          }}
        >
          {testData[selectedTestBox] ? (
            Object.keys(testData[selectedTestBox]).map((sensorKey) => (
              <SensorGroup
                key={sensorKey}
                canEdit={true}
                sensorKey={sensorKey}
                sensors={testData[selectedTestBox][sensorKey]}
                onUpdate={(updatedRows) => updateSensorGroup(sensorKey, updatedRows)}
              />
            ))
          ) : (
            <Typography variant='body2' color='text.secondary' sx={{ p: 2, textAlign: 'center' }}>
              No test boxes with assigned sensors yet.
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  )
}

export default UpdateTestBoxes;